<script setup>
    import { computed, onMounted, onUnmounted, ref } from 'vue'

    const props = defineProps({
        align: {
            type: String,
            default: 'right',
        },
        width: {
            type: String,
            default: '48',
        },
        direction: {
            type: String,
            default: 'down',
            validator: (value) => ['up', 'down'].includes(value),
        },
        contentClasses: {
            type: Array,
            default: () => ['py-1', 'bg-white'],
        },
    })

    const open = ref(false)

    const closeOnEscape = (e) => {
        if (open.value && e.key === 'Escape') {
            open.value = false
        }
    }

    onMounted(() => document.addEventListener('keydown', closeOnEscape))
    onUnmounted(() => document.removeEventListener('keydown', closeOnEscape))

    const widthClass = computed(() => {
        return {
            48: 'w-48',
        }[props.width.toString()]
    })

    const alignmentClasses = computed(() => {
        let classes = ''

        if (props.align === 'left') {
            classes = 'start-0'
        } else if (props.align === 'right') {
            classes = 'end-0'
        }

        if (props.direction === 'up') {
            classes +=
                ' origin-bottom-' + (props.align === 'left' ? 'left' : props.align === 'right' ? 'right' : 'center')
        } else {
            classes += ' origin-top-' + (props.align === 'left' ? 'left' : props.align === 'right' ? 'right' : 'center')
        }

        return classes.trim()
    })

    const positionClasses = computed(() => {
        return props.direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
    })
</script>

<template>
    <div class="relative">
        <div
            class="cursor-pointer"
            @click="open = !open"
        >
            <slot name="trigger" />
        </div>

        <!-- Full Screen Dropdown Overlay -->
        <div
            v-show="open"
            class="fixed inset-0 z-40"
            @click="open = false"
        />

        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div
                v-show="open"
                class="absolute z-50 m-1 rounded-md shadow-lg"
                :class="[widthClass, alignmentClasses, positionClasses]"
                style="display: none"
                @click="open = false"
            >
                <div
                    class="ring-opacity-5 rounded-md ring-1 ring-green-500"
                    :class="contentClasses"
                >
                    <slot name="content" />
                </div>
            </div>
        </transition>
    </div>
</template>
